import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import useChallengeStore from '../hooks/useChallenge'
import useFocusedPracticeStore from '../stores/focusedPracticeStore'
import {
  BarChart3,
  Clock,
  Target,
  TrendingUp,
  BookOpen,
  CheckCircle,
  XCircle,
  Download,
  Share2,
  Search,
  Filter,
  Eye,
  Lock,
  Info,
  Play,
  PlayCircle,
  Scale,
  AlertTriangle,
  Award,
  Zap,
  Trophy,
  Sparkles,
  Calculator
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import BadgeCard from '../components/badges/BadgeCard'
import QuestionDetailModal from '../components/QuestionDetailModal'
import LevelUpAnimation from '../components/LevelUpAnimation'
import CoachFeedbackSection from '../components/CoachFeedbackSection'
import toast from 'react-hot-toast'
import { getErrorMessage } from '../utils/errorUtils.js'
import api from '../services/api'
import focusedPracticeService from '../services/focusedPracticeService'
import { getChallengeSessionPath } from '../utils/navigation'
import { createLogger } from '../utils/logger'
import { useAuthStore } from '../stores/authStore'
import { getPromotionMessage } from '../utils/promotionUtils'

const logger = createLogger('ChallengeReport')

const ChallengeReport = () => {
  // Support both URL formats: /challenge/:sessionId/report and /reports/:challengeId (UC-108)
  const { sessionId, challengeId } = useParams()
  const effectiveChallengeId = sessionId || challengeId
  const navigate = useNavigate()
  const { getChallengeReport, formatTime, isLoading, createAndStartChallenge } = useChallengeStore()
  const { user: currentUser } = useAuthStore()

  const [session, setSession] = useState(null)
  const [report, setReport] = useState(null)
  const [questions, setQuestions] = useState([])
  const [questionsLoading, setQuestionsLoading] = useState(false)
  const [reportLoading, setReportLoading] = useState(true)
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(null)
  const [showQuestionModal, setShowQuestionModal] = useState(false)

  // UC-108: Access level state
  const [accessLevel, setAccessLevel] = useState(null) // 'full' | 'summary'
  // Whether the viewer is the student who took this challenge. Distinct from accessLevel: full
  // access is also granted to admins, guardians and tutors, but the student-only actions below
  // (Level Up / focused practice) are scoped to the acting user server-side and must not be
  // offered to anyone else.
  const [isOwner, setIsOwner] = useState(false)
  const [privacyNotice, setPrivacyNotice] = useState(null)
  const [creatorName, setCreatorName] = useState(null)
  const [strategyType, setStrategyType] = useState(null)
  const [isCreatingChallenge, setIsCreatingChallenge] = useState(false)
  const [promotionInfo, setPromotionInfo] = useState(null)
  const [badgesEarned, setBadgesEarned] = useState([])
  const [showLevelUpAnimation, setShowLevelUpAnimation] = useState(false)

  // Filter states
  const [searchTerm, setSearchTerm] = useState('')
  const [filterDomain, setFilterDomain] = useState('all')
  const [filterSkill, setFilterSkill] = useState('all')
  const [filterDifficulty, setFilterDifficulty] = useState('all')
  const [filterStage, setFilterStage] = useState('all')
  const [filterCorrectness, setFilterCorrectness] = useState('all')
  // Alternative-solution filter (Desmos / second-method questions), persisted per the grid convention.
  const [filterAltSolution, setFilterAltSolution] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('challenge:report:grid') || '{}').altSolution || 'all'
    } catch {
      return 'all'
    }
  })
  useEffect(() => {
    try {
      // Merge into the existing grid config so we don't clobber other keys stored under it.
      const prev = JSON.parse(localStorage.getItem('challenge:report:grid') || '{}')
      localStorage.setItem(
        'challenge:report:grid',
        JSON.stringify({ ...prev, altSolution: filterAltSolution })
      )
    } catch {
      /* ignore quota errors */
    }
  }, [filterAltSolution])

  // Focused Practice state (UC-110)
  const { createSession: createFocusedPracticeSession } = useFocusedPracticeStore()
  const [existingFocusedPracticeDomains, setExistingFocusedPracticeDomains] = useState(new Set())
  const [creatingFocusedPracticeFor, setCreatingFocusedPracticeFor] = useState(null)

  useEffect(() => {
    loadSummaryAndReport()
  }, [effectiveChallengeId])

  /**
   * UC-108: Load summary first to determine access level, then load full details if authorized
   */
  const loadSummaryAndReport = async () => {
    setReportLoading(true)
    try {
      // Step 1: Call /summary endpoint to get access level
      const summaryResponse = await api.get(`/challenges/${effectiveChallengeId}/summary`)

      if (summaryResponse.data.success) {
        const {
          hasFullAccess,
          isOwner: viewerIsOwner,
          privacyNotice: notice,
          data
        } = summaryResponse.data

        // Set access level and privacy notice
        setAccessLevel(hasFullAccess ? 'full' : 'summary')
        // Fall back to comparing the creator id against the signed-in user when an older server
        // response omits isOwner.
        const viewerOwnsReport =
          viewerIsOwner ?? Boolean(currentUser?.id && data.challenge.creator?.id === currentUser.id)
        setIsOwner(viewerOwnsReport)
        setPrivacyNotice(notice)
        setCreatorName(data.challenge.creator?.name)
        setStrategyType(data.challenge.strategyType)

        // Extract stage statistics for time and question counts
        const stageStats = data.statistics?.stageStatistics || []
        const exploratoryStage = stageStats.find((s) => s.factoryType === 'EXPLORATORY')
        const adaptiveStage = stageStats.find((s) => s.factoryType === 'ADAPTIVE')

        // Build session and report from summary data
        const summarySession = {
          id: data.challenge.id,
          name: data.challenge.name,
          status: data.challenge.status,
          completedAt: data.challenge.completedAt || data.challenge.cancelledAt,
          totalScore: data.challenge.totalScore || data.statistics?.accuracy || 0,
          totalTime: data.challenge.totalTime || data.statistics?.totalTimeSpent || 0,
          totalQuestions: data.challenge.totalQuestions || data.statistics?.totalQuestions || 0,
          correctAnswers: data.challenge.correctAnswers || data.statistics?.totalCorrect || 0,
          startingLevel: data.challenge.startingLevel,
          endingLevel: data.challenge.endingLevel,
          exploratoryScore: data.challenge.exploratoryScore,
          adaptiveScore: data.challenge.adaptiveScore,
          // Extract time and question counts from stage statistics
          // Use nullish coalescing to preserve 0 as a valid value
          exploratoryTime:
            data.statistics?.timeDistribution?.exploratory ?? exploratoryStage?.totalTimeSpent ?? 0,
          adaptiveTime:
            data.statistics?.timeDistribution?.adaptive ?? adaptiveStage?.totalTimeSpent ?? 0,
          exploratoryQuestionCount:
            exploratoryStage?.totalQuestions ?? exploratoryStage?.totalAnswered ?? null,
          adaptiveQuestionCount:
            adaptiveStage?.totalQuestions ?? adaptiveStage?.totalAnswered ?? null,
          stages: data.challenge.stages,
          creator: data.challenge.creator
        }

        const summaryReport = {
          overallScore: data.results?.overallScore,
          performanceLevel: data.results?.performanceLevel,
          scaledScore: data.results?.satScore?.totalScore,
          satScore: data.results?.satScore,
          detailedBreakdown: data.results?.detailedBreakdown,
          statistics: data.statistics
        }

        setSession(summarySession)
        setReport(summaryReport)
        setPromotionInfo(data.promotionInfo || null)
        setBadgesEarned(data.badgesEarned || [])

        // Trigger the level up animation if promoted - only for the student who earned it. For any
        // other reader the promotion status card reports the same outcome in the third person, so
        // suppressing the animation here must not also suppress that card.
        if (data.promotionInfo?.wasPromoted && viewerOwnsReport) {
          setShowLevelUpAnimation(true)
        }

        // Step 2: If full access, also load questions
        if (hasFullAccess) {
          await loadQuestions()
        }
      } else {
        toast.error(getErrorMessage(summaryResponse.data.error))
        navigate('/dashboard')
      }
    } catch (error) {
      logger.error('Error loading report summary:', error)
      // Handle specific error codes
      if (error.response?.status === 403) {
        const errorCode = error.response?.data?.code
        if (errorCode === 'UNAUTHORIZED_REPORT_ACCESS') {
          toast.error('You are not authorized to view this report')
        } else if (errorCode === 'CHALLENGE_NOT_TERMINAL') {
          toast.error('This challenge has not been completed yet')
        } else if (errorCode === 'UNAUTHORIZED_FULL_ACCESS') {
          toast.error('You do not have access to the detailed questions for this report')
        } else {
          toast.error('Access denied')
        }
      } else if (error.response?.status === 404) {
        toast.error('Challenge not found')
      } else if (error.response?.status === 400) {
        const errorCode = error.response?.data?.code
        if (errorCode === 'INVALID_CHALLENGE_ID') {
          toast.error('Invalid challenge ID')
        } else {
          toast.error(getErrorMessage(error))
        }
      } else {
        toast.error(getErrorMessage(error))
      }
      navigate('/dashboard')
    } finally {
      setReportLoading(false)
    }
  }

  /**
   * Load full question details (only called when hasFullAccess = true)
   */
  const loadQuestions = async () => {
    setQuestionsLoading(true)
    try {
      // Fetch all question responses for this completed challenge
      const response = await api.get(`/challenges/${effectiveChallengeId}/results`)

      if (response.data.success) {
        // Extract questions from stages
        const challenge = response.data.data.challenge
        const allQuestions = []

        if (challenge.stages) {
          for (const stage of challenge.stages) {
            if (stage.questionResponses) {
              // Use a Set to track unique question IDs per stage
              const uniqueQuestionIds = new Set()
              const totalResponses = stage.questionResponses.length

              stage.questionResponses.forEach((qr) => {
                const questionId = qr.question_id || qr.questionId
                if (questionId && !uniqueQuestionIds.has(questionId)) {
                  uniqueQuestionIds.add(questionId)
                  allQuestions.push({
                    ...qr,
                    stageName: stage.name,
                    stageType: stage.factoryType
                  })
                }
              })

              // Log stage information for debugging
              logger.debug(
                `Stage: ${stage.name}, Total responses: ${totalResponses}, Unique questions: ${uniqueQuestionIds.size}`
              )
              if (totalResponses !== uniqueQuestionIds.size) {
                logger.warn(
                  `⚠️ Duplicate questions detected in ${stage.name}: ${totalResponses} responses, ${uniqueQuestionIds.size} unique questions`
                )
              }
            }
          }
        }

        setQuestions(allQuestions)
      }
    } catch (error) {
      logger.error('Error loading questions:', error)
      // loadQuestions is only called when hasFullAccess=true, so a failure here
      // indicates a server issue, not an auth problem
      if (error.response?.status === 403) {
        toast.error('Session expired. Please refresh the page.')
      } else {
        toast.error('Failed to load question details. Please try refreshing the page.')
      }
    } finally {
      setQuestionsLoading(false)
    }
  }

  // Get unique values for filters
  const uniqueDomains = useMemo(() => {
    return [...new Set(questions.map((q) => q.domain?.domain_name || q.domain).filter(Boolean))]
  }, [questions])

  const uniqueSkills = useMemo(() => {
    return [...new Set(questions.map((q) => q.skill?.skill_name || q.skill).filter(Boolean))]
  }, [questions])

  const uniqueDifficulties = useMemo(() => {
    return [...new Set(questions.map((q) => q.difficulty).filter(Boolean))]
  }, [questions])

  const uniqueStages = useMemo(() => {
    return [...new Set(questions.map((q) => q.stageName).filter(Boolean))]
  }, [questions])

  // Filter and sort questions
  const filteredQuestions = useMemo(() => {
    return questions.filter((q) => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase()
        const matchesSearch =
          q.id?.toString().includes(searchLower) ||
          q.question_id?.toString().includes(searchLower) ||
          q.questionId?.toString().includes(searchLower) ||
          (q.domain?.domain_name || q.domain)?.toLowerCase().includes(searchLower) ||
          (q.skill?.skill_name || q.skill)?.toLowerCase().includes(searchLower)
        if (!matchesSearch) return false
      }

      // Domain filter
      if (filterDomain !== 'all' && (q.domain?.domain_name || q.domain) !== filterDomain)
        return false

      // Skill filter
      if (filterSkill !== 'all' && (q.skill?.skill_name || q.skill) !== filterSkill) return false

      // Difficulty filter
      if (filterDifficulty !== 'all' && q.difficulty !== filterDifficulty) return false

      // Stage filter
      if (filterStage !== 'all' && q.stageName !== filterStage) return false

      // Correctness filter
      if (filterCorrectness !== 'all') {
        const isCorrect = q.isCorrect || q.is_correct
        if (filterCorrectness === 'correct' && !isCorrect) return false
        if (filterCorrectness === 'incorrect' && isCorrect) return false
      }

      // Alternative-solution filter (degrades for v1 questions: hasAlternativeSolution falls back to
      // the desmos state / rationale-text heading on the server, so older questions still match).
      if (filterAltSolution !== 'all') {
        if (!q.hasAlternativeSolution) return false
        if (filterAltSolution === 'desmos' && q.alternativeSolutionType !== 'desmos') return false
      }

      return true
    })
  }, [
    questions,
    searchTerm,
    filterDomain,
    filterSkill,
    filterDifficulty,
    filterStage,
    filterCorrectness,
    filterAltSolution
  ])

  const selectedQuestion =
    selectedQuestionIndex !== null ? filteredQuestions[selectedQuestionIndex] : null

  const handleQuestionClick = (index) => {
    setSelectedQuestionIndex(index)
    setShowQuestionModal(true)
  }

  const handleQuestionNavigate = (newIndex) => {
    if (newIndex >= 0 && newIndex < filteredQuestions.length) {
      setSelectedQuestionIndex(newIndex)
    }
  }

  const handleQuickFilter = (type) => {
    setFilterCorrectness(type)
    // Reset other filters
    setSearchTerm('')
  }

  const getScoreColor = (score, maxScore = 800) => {
    const percentage = (score / maxScore) * 100
    if (percentage >= 80) return 'text-success-600'
    if (percentage >= 60) return 'text-warning-600'
    return 'text-error-600'
  }

  const getScoreBadge = (score, maxScore = 800) => {
    const percentage = (score / maxScore) * 100
    if (percentage >= 80) return 'badge-success'
    if (percentage >= 60) return 'badge-warning'
    return 'badge-error'
  }

  const getSATScoreColor = (score) => {
    if (score >= 1400) return 'text-success-600'
    if (score >= 1200) return 'text-warning-600'
    if (score >= 1000) return 'text-primary-600'
    return 'text-error-600'
  }

  const getSATScoreBadge = (score) => {
    if (score >= 1400) return 'badge-success'
    if (score >= 1200) return 'badge-warning'
    if (score >= 1000) return 'badge-primary'
    return 'badge-error'
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  /**
   * Calculate accuracy percentage from stats object
   * @param {Object} stats - Stats with total and correct counts
   * @returns {number} Accuracy as percentage (0-100), 0 if invalid
   */
  const calculateAccuracy = (stats) => {
    if (!stats || stats.total === 0) return 0
    const accuracy = (stats.correct / stats.total) * 100
    return isNaN(accuracy) ? 0 : accuracy
  }

  /**
   * Format score as percentage string (integer, no decimals)
   * @param {number|null|undefined} score - Score to format
   * @returns {string} Formatted score or "0" if invalid
   */
  const formatScore = (score) => {
    if (score === null || score === undefined || isNaN(score)) return '0'
    return Math.floor(score).toString()
  }

  /**
   * Check if overall score meets the promotion threshold
   * Memoized to avoid recalculation on each render
   */
  const meetsOverallThreshold = useMemo(() => {
    if (!promotionInfo?.criteria?.overallThreshold) return true
    return (session?.totalScore ?? 0) >= promotionInfo.criteria.overallThreshold
  }, [promotionInfo?.criteria?.overallThreshold, session?.totalScore])

  /**
   * Check if a domain score meets the promotion threshold
   */
  const meetsDomainThreshold = (accuracy) => {
    if (!promotionInfo?.criteria?.domainThreshold) return true
    return accuracy >= promotionInfo.criteria.domainThreshold
  }

  /**
   * Check if all domains meet the threshold (for strategies that require it)
   * Memoized to avoid recalculation on each render
   */
  const allDomainsMeetThreshold = useMemo(() => {
    if (!promotionInfo?.criteria?.domainThreshold || !promotionInfo?.criteria?.requireAllDomains) {
      return true
    }
    if (!report?.statistics?.domainStatistics) return true

    return Object.values(report.statistics.domainStatistics).every((stats) => {
      const accuracy = calculateAccuracy(stats)
      return accuracy >= promotionInfo.criteria.domainThreshold
    })
  }, [
    promotionInfo?.criteria?.domainThreshold,
    promotionInfo?.criteria?.requireAllDomains,
    report?.statistics?.domainStatistics
  ])

  /**
   * Get human-readable description of promotion requirements
   * Memoized to avoid recalculation on each render
   */
  const promotionRequirementsDescription = useMemo(() => {
    const criteria = promotionInfo?.criteria
    if (!criteria) return null

    // SAT Mini: 100% required
    if (criteria.overallThreshold === 100 && !criteria.domainThreshold) {
      return 'Perfect score (100%) required for promotion'
    }

    // Regular SAT: 90% overall + 80% per domain
    if (criteria.domainThreshold && criteria.requireAllDomains) {
      return `${criteria.overallThreshold}% overall AND ${criteria.domainThreshold}% minimum in each domain required`
    }

    // Default: just overall threshold
    return `${criteria.overallThreshold}% overall score required`
  }, [promotionInfo?.criteria])

  /**
   * Promotion status message.
   *
   * Delegates to the shared, unit-tested helper rather than duplicating its ~100 lines here (the
   * page previously carried a byte-for-byte copy, so the extracted util was dead code and the live
   * copy was untested). subjectName switches the copy to the third person for a tutor, guardian or
   * admin reader, who did not take this challenge.
   */
  const promotionMessage = useMemo(
    () =>
      getPromotionMessage({
        promotionInfo,
        session,
        domainStatistics: report?.statistics?.domainStatistics,
        subjectName: isOwner ? null : creatorName
      }),
    [promotionInfo, session, report?.statistics?.domainStatistics, isOwner, creatorName]
  )

  const handlePrint = () => {
    window.print()
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Deaveris Challenge Results',
          text: `I scored ${session.totalScore}% on my adaptive challenge!`,
          url: window.location.href
        })
      } catch (error) {
        logger.debug('Error sharing:', error)
      }
    } else {
      // Fallback: copy to clipboard
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(window.location.href)
          toast.success('Link copied to clipboard!')
        } else {
          // Fallback for older browsers
          const textArea = document.createElement('textarea')
          textArea.value = window.location.href
          document.body.appendChild(textArea)
          textArea.select()
          document.execCommand('copy')
          document.body.removeChild(textArea)
          toast.success('Link copied to clipboard!')
        }
      } catch (error) {
        logger.error('Failed to copy link:', error)
        toast.error('Failed to copy link. Please copy manually.')
      }
    }
  }

  const handleStartNewChallenge = async () => {
    if (!strategyType) {
      toast.error('Unable to determine challenge type')
      return
    }

    setIsCreatingChallenge(true)
    try {
      const result = await createAndStartChallenge({ strategyType })
      if (result.success) {
        toast.success('Starting new challenge...')
        navigate(getChallengeSessionPath(result.challenge?.id), { replace: true })
      } else {
        toast.error(result.error || 'Failed to start challenge')
      }
    } catch (error) {
      logger.error('Error starting new challenge:', error)
      toast.error('Failed to start challenge')
    } finally {
      setIsCreatingChallenge(false)
    }
  }

  /**
   * UC-110: Create focused practice session for a specific domain
   * First checks if session exists, then navigates or creates accordingly
   */
  const handleCreateFocusedPractice = async (domainName) => {
    if (!effectiveChallengeId || !domainName) return

    setCreatingFocusedPracticeFor(domainName)
    try {
      // First check if session already exists
      const checkResult = await focusedPracticeService.checkExistingSession(
        effectiveChallengeId,
        domainName
      )

      if (checkResult.success && checkResult.data.exists) {
        const existingSession = checkResult.data.session
        setExistingFocusedPracticeDomains((prev) => new Set([...prev, domainName]))

        // Navigate based on session status
        switch (existingSession.status) {
          case 'pending':
          case 'pre_practice':
            navigate(`/focused-practice/${existingSession.id}/review`)
            toast.success(`Continuing practice for ${domainName}`)
            break
          case 'in_progress':
            navigate(`/focused-practice/${existingSession.id}/practice`)
            toast.success(`Resuming practice for ${domainName}`)
            break
          case 'completed':
            navigate(`/focused-practice/${existingSession.id}/results`)
            toast.success(`Viewing results for ${domainName}`)
            break
          default:
            navigate(`/focused-practice/${existingSession.id}/review`)
        }
        return
      }

      // No existing session, create a new one
      const result = await createFocusedPracticeSession({
        sourceChallengeId: effectiveChallengeId,
        targetDomain: domainName
      })

      if (result.success) {
        toast.success(`Focused practice created for ${domainName}`)
        setExistingFocusedPracticeDomains((prev) => new Set([...prev, domainName]))
        navigate(`/focused-practice/${result.data.id}/review`)
      } else {
        toast.error(getErrorMessage(result.error))
      }
    } catch (error) {
      logger.error('Error creating focused practice:', error)
      toast.error(getErrorMessage(error))
    } finally {
      setCreatingFocusedPracticeFor(null)
    }
  }

  /**
   * Check if a domain has accuracy below the focused practice threshold (80%)
   */
  const needsFocusedPractice = (accuracy) => {
    return accuracy < 80
  }

  if (isLoading || reportLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!session || !report) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          Report not found
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          The challenge report could not be loaded.
        </p>
        <button onClick={() => navigate('/dashboard')} className="btn-primary">
          Go to Dashboard
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* UC-108: Privacy Notice Banner for summary-only access */}
      {accessLevel === 'summary' && privacyNotice && (
        <div className="bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-500 p-4 rounded-r-lg">
          <div className="flex items-start">
            <Lock className="w-5 h-5 text-primary-500 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <p className="text-primary-700 dark:text-primary-300 font-medium">Summary View</p>
              <p className="text-primary-600 dark:text-primary-400 text-sm mt-1">{privacyNotice}</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="card overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-primary-500 via-purple-500 to-pink-500" />
        <div className="card-body">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Challenge Report
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {creatorName && accessLevel === 'summary' && (
                  <span className="font-medium">{creatorName}&apos;s challenge • </span>
                )}
                Completed on {formatDate(session.completedAt)}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <button
                onClick={handlePrint}
                className="btn-secondary flex items-center gap-2 px-3 py-2"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Print</span>
              </button>
              <button
                onClick={handleShare}
                className="btn-secondary flex items-center gap-2 px-3 py-2"
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">Share</span>
              </button>
              {accessLevel === 'full' && strategyType && strategyType !== 'FOCUSED_PRACTICE' && (
                <button
                  onClick={handleStartNewChallenge}
                  disabled={isCreatingChallenge}
                  className="btn-primary flex items-center gap-2 px-3 sm:px-4 py-2"
                >
                  {isCreatingChallenge ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span className="hidden sm:inline">Starting...</span>
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      <span className="hidden sm:inline">Start New Challenge</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Overall Score */}
      <div className="card overflow-hidden">
        <div className="h-1.5 bg-gradient-to-r from-primary-500 via-purple-500 to-pink-500" />
        <div className="card-body">
          <div className="flex flex-col md:flex-row md:items-center md:gap-8">
            {/* Animated Circular Progress */}
            <div className="flex justify-center md:justify-start mb-4 md:mb-0">
              <div className="relative w-24 h-24 sm:w-28 sm:h-28 flex-shrink-0">
                {(() => {
                  const score = session.totalScore || 0
                  const circumference = 2 * Math.PI * 54
                  const strokeDashoffset = circumference * (1 - score / 100)
                  const getGradientId = () => {
                    if (score >= 80) return 'scoreGradientHigh'
                    if (score >= 60) return 'scoreGradientMedium'
                    return 'scoreGradientLow'
                  }
                  const showBelowThreshold =
                    !report?.scaledScore &&
                    promotionInfo?.criteria?.overallThreshold &&
                    !meetsOverallThreshold

                  return (
                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 128 128">
                      {/* Gradient definitions */}
                      <defs>
                        <linearGradient id="scoreGradientHigh" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#10b981" />
                          <stop offset="100%" stopColor="#2dd4bf" />
                        </linearGradient>
                        <linearGradient id="scoreGradientMedium" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#3b82f6" />
                          <stop offset="100%" stopColor="#8b5cf6" />
                        </linearGradient>
                        <linearGradient id="scoreGradientLow" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#f59e0b" />
                          <stop offset="100%" stopColor="#fb923c" />
                        </linearGradient>
                        <linearGradient id="scoreGradientError" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#ef4444" />
                          <stop offset="100%" stopColor="#f87171" />
                        </linearGradient>
                      </defs>

                      {/* Background track */}
                      <circle
                        cx="64"
                        cy="64"
                        r="54"
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="none"
                        className="text-gray-200 dark:text-gray-700"
                      />

                      {/* Threshold marker */}
                      {promotionInfo?.criteria?.overallThreshold &&
                        !report?.scaledScore &&
                        (() => {
                          const markerWidth = 0.02
                          const markerPosition = promotionInfo.criteria.overallThreshold / 100
                          const offset =
                            circumference * (1 - markerPosition) + circumference * (markerWidth / 2)
                          return (
                            <circle
                              cx="64"
                              cy="64"
                              r="54"
                              stroke="currentColor"
                              strokeWidth="8"
                              fill="none"
                              strokeDasharray={`${circumference * markerWidth} ${circumference * (1 - markerWidth)}`}
                              strokeDashoffset={offset}
                              className="text-gray-400 dark:text-gray-500"
                              strokeLinecap="butt"
                            />
                          )
                        })()}

                      {/* Animated progress arc */}
                      <circle
                        cx="64"
                        cy="64"
                        r="54"
                        stroke={
                          showBelowThreshold
                            ? 'url(#scoreGradientError)'
                            : `url(#${getGradientId()})`
                        }
                        strokeWidth="8"
                        fill="none"
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                        strokeLinecap="butt"
                        className="transition-all duration-1000 ease-out"
                      />
                    </svg>
                  )
                })()}

                {/* Center content */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div
                      className={`text-xl sm:text-2xl font-bold transition-colors duration-300 ${
                        !report?.scaledScore &&
                        promotionInfo?.criteria?.overallThreshold &&
                        !meetsOverallThreshold
                          ? 'text-error-600 dark:text-error-400'
                          : session.totalScore >= 80
                            ? 'text-success-600 dark:text-success-400'
                            : session.totalScore >= 60
                              ? 'text-primary-600 dark:text-primary-400'
                              : 'text-warning-600 dark:text-warning-400'
                      }`}
                    >
                      {report?.scaledScore || formatScore(session.totalScore)}%
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                      {report?.scaledScore ? 'SAT Score' : 'Overall'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Content section - right side on desktop */}
            <div className="flex-1 text-center md:text-left">
              {/* Motivational headline */}
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2 animate-fade-in">
                {report?.scaledScore
                  ? report.scaledScore >= 1400
                    ? 'Excellent!'
                    : report.scaledScore >= 1200
                      ? 'Great Job!'
                      : report.scaledScore >= 1000
                        ? 'Good Work!'
                        : 'Keep Practicing!'
                  : session.totalScore >= 80
                    ? 'Excellent!'
                    : session.totalScore >= 60
                      ? 'Good Job!'
                      : 'Keep Practicing!'}
              </h2>

              {/* Score summary */}
              <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base mb-4">
                {report?.scaledScore
                  ? `You scored ${report.scaledScore} out of 800 on the ${report?.sectionName || 'SAT'} section`
                  : `You scored ${formatScore(session.totalScore)}% overall`}
              </p>

              {/* Level Up Animation - owner only: it celebrates the reader's own promotion, which
                  would misattribute the achievement when a tutor or guardian is looking. The
                  promotion status card below still reports the same outcome, in the third person. */}
              {isOwner &&
                showLevelUpAnimation &&
                session?.startingLevel &&
                session?.endingLevel && (
                  <div className="w-full mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <LevelUpAnimation
                      fromLevel={session.startingLevel}
                      toLevel={session.endingLevel}
                      onComplete={() => setShowLevelUpAnimation(false)}
                    />
                  </div>
                )}

              {/* Promotion Status Message */}
              {promotionInfo?.supportsLevelProgression && !showLevelUpAnimation && (
                <div className="w-full mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div
                    className={`p-5 rounded-xl animate-fade-in-up ${
                      promotionInfo.wasPromoted
                        ? 'bg-gradient-to-br from-success-50 to-emerald-50 dark:from-success-900/20 dark:to-emerald-900/20 border border-success-200 dark:border-success-800'
                        : promotionInfo.wasDemoted
                          ? 'bg-gradient-to-br from-warning-50 to-amber-50 dark:from-warning-900/20 dark:to-amber-900/20 border border-warning-200 dark:border-warning-800'
                          : 'bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 border border-primary-200 dark:border-primary-800'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Icon container */}
                      <div
                        className={`p-2.5 rounded-xl flex-shrink-0 ${
                          promotionInfo.wasPromoted
                            ? 'bg-white/60 dark:bg-gray-900/40 text-success-600 dark:text-success-400'
                            : promotionInfo.wasDemoted
                              ? 'bg-white/60 dark:bg-gray-900/40 text-warning-600 dark:text-warning-400'
                              : 'bg-white/60 dark:bg-gray-900/40 text-primary-600 dark:text-primary-400'
                        }`}
                      >
                        {promotionInfo.wasPromoted ? (
                          <Award className="w-6 h-6" />
                        ) : promotionInfo.wasDemoted ? (
                          <AlertTriangle className="w-6 h-6" />
                        ) : (
                          <Info className="w-6 h-6" />
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0 text-left">
                        <h4
                          className={`font-semibold text-lg ${
                            promotionInfo.wasPromoted
                              ? 'text-success-800 dark:text-success-200'
                              : promotionInfo.wasDemoted
                                ? 'text-warning-800 dark:text-warning-200'
                                : 'text-primary-800 dark:text-primary-200'
                          }`}
                        >
                          {promotionMessage.title}
                        </h4>
                        <p
                          className={`mt-1 text-sm ${
                            promotionInfo.wasPromoted
                              ? 'text-success-700 dark:text-success-300'
                              : promotionInfo.wasDemoted
                                ? 'text-warning-700 dark:text-warning-300'
                                : 'text-primary-700 dark:text-primary-300'
                          }`}
                        >
                          {promotionMessage.message}
                        </p>

                        {/* Promotion requirements checklist */}
                        {promotionInfo.criteria && !promotionInfo.wasPromoted && (
                          <div className="mt-4 pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
                            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
                              Promotion Requirements
                            </p>
                            <ul className="space-y-2">
                              {/* Overall score requirement */}
                              <li className="flex items-center gap-2 text-sm">
                                {meetsOverallThreshold ? (
                                  <CheckCircle className="w-4 h-4 text-success-500 flex-shrink-0" />
                                ) : (
                                  <XCircle className="w-4 h-4 text-error-500 flex-shrink-0" />
                                )}
                                <span className="text-gray-700 dark:text-gray-300 flex-1">
                                  {promotionInfo.criteria.overallThreshold === 100
                                    ? 'Perfect score (100%)'
                                    : `Overall: ${promotionInfo.criteria.overallThreshold}%`}
                                </span>
                                {session.totalScore !== undefined && (
                                  <span
                                    className={`font-medium ${
                                      meetsOverallThreshold
                                        ? 'text-success-600 dark:text-success-400'
                                        : 'text-error-600 dark:text-error-400'
                                    }`}
                                  >
                                    {formatScore(session.totalScore)}%
                                  </span>
                                )}
                              </li>

                              {/* Domain threshold requirement */}
                              {promotionInfo.criteria.domainThreshold &&
                                promotionInfo.criteria.requireAllDomains && (
                                  <li className="flex items-center gap-2 text-sm">
                                    {allDomainsMeetThreshold ? (
                                      <CheckCircle className="w-4 h-4 text-success-500 flex-shrink-0" />
                                    ) : (
                                      <XCircle className="w-4 h-4 text-error-500 flex-shrink-0" />
                                    )}
                                    <span className="text-gray-700 dark:text-gray-300 flex-1">
                                      Each domain: min {promotionInfo.criteria.domainThreshold}%
                                    </span>
                                    <span
                                      className={`font-medium ${
                                        allDomainsMeetThreshold
                                          ? 'text-success-600 dark:text-success-400'
                                          : 'text-error-600 dark:text-error-400'
                                      }`}
                                    >
                                      {allDomainsMeetThreshold ? 'All met' : 'Some below'}
                                    </span>
                                  </li>
                                )}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Badges */}
              <div className="flex flex-wrap justify-center md:justify-start gap-2 mt-4">
                {report?.scaledScore ? (
                  <>
                    <span
                      className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold shadow-sm transition-all duration-200 hover:shadow-md ${
                        report.scaledScore >= 1400
                          ? 'bg-gradient-to-r from-success-500 to-emerald-500 text-white'
                          : report.scaledScore >= 1200
                            ? 'bg-gradient-to-r from-primary-500 to-indigo-500 text-white'
                            : report.scaledScore >= 1000
                              ? 'bg-gradient-to-r from-warning-500 to-amber-500 text-white'
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {report.scaledScore >= 1400
                        ? 'Advanced'
                        : report.scaledScore >= 1200
                          ? 'Proficient'
                          : report.scaledScore >= 1000
                            ? 'Developing'
                            : 'Needs Improvement'}
                    </span>
                    <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-primary-500 to-purple-600 text-white shadow-sm transition-all duration-200 hover:shadow-md">
                      {report?.percentile || 0}th Percentile
                    </span>
                  </>
                ) : (
                  <>
                    <span
                      className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold shadow-sm transition-all duration-200 hover:shadow-md ${
                        session.totalScore >= 80
                          ? 'bg-gradient-to-r from-success-500 to-emerald-500 text-white'
                          : session.totalScore >= 60
                            ? 'bg-gradient-to-r from-warning-500 to-amber-500 text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {session.totalScore >= 80
                        ? 'Advanced'
                        : session.totalScore >= 60
                          ? 'Proficient'
                          : 'Developing'}
                    </span>
                    <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-primary-500 to-purple-600 text-white shadow-sm transition-all duration-200 hover:shadow-md">
                      Level {session.endingLevel}
                    </span>
                  </>
                )}
              </div>

              {/* Achievements Earned This Challenge */}
              {badgesEarned.length > 0 && (
                <div className="w-full mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-5 h-5 text-yellow-500" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Achievements Earned!
                    </h3>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {badgesEarned.map((badge) => (
                      <BadgeCard key={badge.code} badge={badge} earnedAt={badge.earnedAt} />
                    ))}
                  </div>
                  <button
                    onClick={() => navigate('/profile#achievements')}
                    className="mt-4 text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                  >
                    <Trophy className="w-4 h-4" />
                    View all achievements
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Score Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card">
          <div className="card-body text-center">
            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
              <BookOpen className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Exploratory Phase
            </h3>
            <div className={`text-2xl font-bold mb-2 ${getScoreColor(session.exploratoryScore)}`}>
              {session.exploratoryScore}%
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {formatTime(session.exploratoryTime)} • {session.exploratoryQuestionCount ?? '?'}{' '}
              questions
            </p>
          </div>
        </div>

        <div className="card">
          <div className="card-body text-center">
            <div className="w-12 h-12 bg-success-100 dark:bg-success-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
              <Target className="w-6 h-6 text-success-600 dark:text-success-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Adaptive Phase
            </h3>
            <div className={`text-2xl font-bold mb-2 ${getScoreColor(session.adaptiveScore)}`}>
              {session.adaptiveScore}%
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {formatTime(session.adaptiveTime)} • {session.adaptiveQuestionCount ?? '?'} questions
            </p>
          </div>
        </div>

        <div className="card">
          <div className="card-body text-center">
            <div className="w-12 h-12 bg-warning-100 dark:bg-warning-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
              <TrendingUp className="w-6 h-6 text-warning-600 dark:text-warning-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Level Progression
            </h3>
            <div className="text-2xl font-bold mb-2 text-gray-900 dark:text-gray-100">
              {session.startingLevel} → {session.endingLevel}
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {session.endingLevel > session.startingLevel ? '+1 Level' : 'No change'}
            </p>
          </div>
        </div>

        {/* SAT Score Card */}
        {report?.satScore?.sectionScore !== undefined && report?.satScore?.maxPossibleScore > 0 && (
          <div className="card">
            <div className="card-body text-center">
              <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                <Scale className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">
                SAT Score
              </h3>
              <div className="text-2xl font-bold mb-2 text-indigo-600 dark:text-indigo-400">
                {report.satScore.sectionScore} / {report.satScore.maxPossibleScore}
              </div>
              <div className="progress-bar h-2 mb-2">
                <div
                  className="progress-fill bg-indigo-500"
                  style={{
                    width: `${(report.satScore.sectionScore / report.satScore.maxPossibleScore) * 100}%`
                  }}
                />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                Max score of {report.satScore.maxPossibleScore} based on challenge difficulty
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Focused Practice Opportunity Banner - owner only: it prompts an action only the student
          who took the challenge can perform. */}
      {accessLevel === 'full' &&
        isOwner &&
        (() => {
          const domainsNeedingPractice = Object.entries(report.statistics?.domainStatistics || {})
            .filter(([, stats]) => calculateAccuracy(stats) < 80)
            .map(([domain, stats]) => ({ domain, accuracy: calculateAccuracy(stats) }))

          if (domainsNeedingPractice.length === 0) return null

          return (
            <div className="bg-gradient-to-r from-primary-50 to-purple-50 dark:from-primary-900/20 dark:to-purple-900/20 rounded-xl border border-primary-200 dark:border-primary-800 p-5 mb-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center gap-3 flex-1">
                  <div className="p-3 bg-gradient-to-br from-primary-500 to-purple-600 rounded-xl shadow-lg">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {domainsNeedingPractice.length === 1
                        ? 'Level Up Opportunity!'
                        : `${domainsNeedingPractice.length} Areas to Strengthen`}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {domainsNeedingPractice.length === 1
                        ? `${domainsNeedingPractice[0].domain} scored ${formatScore(domainsNeedingPractice[0].accuracy)}% — targeted practice can help you master this.`
                        : 'Focused practice on these domains will boost your score. Click "Level Up" on any domain below 80%.'}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 sm:flex-col sm:items-end">
                  {domainsNeedingPractice.slice(0, 3).map(({ domain, accuracy }) => (
                    <span
                      key={domain}
                      className="inline-flex items-center gap-1.5 px-3 py-1 bg-white dark:bg-gray-800 rounded-full text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700"
                    >
                      <span
                        className={`w-2 h-2 rounded-full ${accuracy < 60 ? 'bg-red-500' : 'bg-amber-500'}`}
                      />
                      {domain}: {formatScore(accuracy)}%
                    </span>
                  ))}
                  {domainsNeedingPractice.length > 3 && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      +{domainsNeedingPractice.length - 3} more
                    </span>
                  )}
                </div>
              </div>
            </div>
          )
        })()}

      {/* Performance Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Performance by Domain */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                Performance by Domain
              </h3>
              {promotionInfo?.criteria?.domainThreshold && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Target: {promotionInfo.criteria.domainThreshold}%
                </span>
              )}
            </div>
          </div>
          <div className="card-body">
            <div className="space-y-3">
              {Object.keys(report.statistics?.domainStatistics || {}).length === 0 ? (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                  No domain data available
                </div>
              ) : (
                Object.entries(report.statistics?.domainStatistics || {}).map(([domain, stats]) => {
                  const accuracy = calculateAccuracy(stats)
                  const meetsThreshold = meetsDomainThreshold(accuracy)
                  const showThresholdWarning =
                    promotionInfo?.criteria?.domainThreshold &&
                    promotionInfo?.criteria?.requireAllDomains &&
                    !meetsThreshold

                  return (
                    <div
                      key={domain}
                      className={`flex items-center justify-between ${
                        showThresholdWarning
                          ? 'bg-red-50 dark:bg-red-900/10 -mx-2 px-2 py-2 rounded-lg border border-red-200 dark:border-red-800'
                          : ''
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate flex items-center">
                            {showThresholdWarning && (
                              <AlertTriangle className="w-4 h-4 text-red-500 mr-1.5 flex-shrink-0" />
                            )}
                            {domain}
                          </span>
                          <span
                            className={`text-sm font-semibold ${
                              showThresholdWarning
                                ? 'text-red-600 dark:text-red-400'
                                : getScoreColor(accuracy)
                            }`}
                          >
                            {formatScore(accuracy)}%
                          </span>
                        </div>
                        <div className="progress-bar h-2 relative">
                          <div
                            className={`progress-fill ${showThresholdWarning ? 'bg-red-500' : ''}`}
                            style={{ width: `${accuracy}%` }}
                          />
                          {/* Threshold marker */}
                          {promotionInfo?.criteria?.domainThreshold && (
                            <div
                              className="absolute top-0 h-full w-0.5 bg-gray-400 dark:bg-gray-500"
                              style={{ left: `${promotionInfo.criteria.domainThreshold}%` }}
                              title={`Target: ${promotionInfo.criteria.domainThreshold}%`}
                            />
                          )}
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                          <span>
                            {stats.correct}/{stats.total} correct
                          </span>
                          {showThresholdWarning && (
                            <span className="text-red-500 dark:text-red-400">
                              Below {promotionInfo.criteria.domainThreshold}% target
                            </span>
                          )}
                        </div>
                      </div>

                      {/* UC-110: Practice button for domains < 80% - Enhanced for engagement.
                          Owner only: focused practice is created for the acting user, so a tutor,
                          guardian or admin clicking this would only get an authorization error. The
                          accuracy data itself stays visible to them. */}
                      {accessLevel === 'full' && isOwner && needsFocusedPractice(accuracy) && (
                        <div className="ml-3 flex-shrink-0">
                          {existingFocusedPracticeDomains.has(domain) ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleCreateFocusedPractice(domain)
                              }}
                              className="px-3 py-1.5 text-xs font-medium bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50 rounded-lg border border-green-200 dark:border-green-700 transition-colors flex items-center gap-1.5"
                            >
                              <PlayCircle className="w-3.5 h-3.5" />
                              Continue Practice
                            </button>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleCreateFocusedPractice(domain)
                              }}
                              disabled={creatingFocusedPracticeFor === domain}
                              className="group relative px-4 py-2 text-sm font-semibold bg-gradient-to-r from-primary-600 to-purple-600 hover:from-primary-700 hover:to-purple-700 text-white rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 animate-pulse hover:animate-none"
                            >
                              {creatingFocusedPracticeFor === domain ? (
                                <>
                                  <LoadingSpinner size="xs" className="text-white" />
                                  <span>Creating...</span>
                                </>
                              ) : (
                                <>
                                  <Target className="w-4 h-4" />
                                  <span>Level Up</span>
                                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full animate-ping" />
                                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full" />
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      )}

                      {/* Show checkmark for domains >= 90% */}
                      {accessLevel === 'full' && !needsFocusedPractice(accuracy) && (
                        <div className="ml-3 flex-shrink-0">
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        </div>
                      )}
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </div>

        {/* Performance by Difficulty */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Performance by Difficulty
            </h3>
          </div>
          <div className="card-body">
            <div className="space-y-3">
              {Object.keys(report.statistics?.difficultyStatistics || {}).length === 0 ? (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                  No difficulty data available
                </div>
              ) : (
                Object.entries(report.statistics?.difficultyStatistics || {}).map(
                  ([difficulty, stats]) => {
                    const accuracy = calculateAccuracy(stats)
                    return (
                      <div key={difficulty} className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {difficulty === 'H' ? 'Hard' : difficulty === 'M' ? 'Medium' : 'Easy'}
                            </span>
                            <span className={`text-sm font-semibold ${getScoreColor(accuracy)}`}>
                              {formatScore(accuracy)}%
                            </span>
                          </div>
                          <div className="progress-bar h-2">
                            <div className="progress-fill" style={{ width: `${accuracy}%` }} />
                          </div>
                          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                            <span>
                              {stats.correct}/{stats.total} correct
                            </span>
                          </div>
                        </div>
                      </div>
                    )
                  }
                )
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      {report.recommendations && report.recommendations.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Recommendations
            </h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              {report.recommendations.map((rec, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                      rec.priority === 'high'
                        ? 'bg-error-100 dark:bg-error-900/30 text-error-600 dark:text-error-400'
                        : rec.priority === 'medium'
                          ? 'bg-warning-100 dark:bg-warning-900/30 text-warning-600 dark:text-warning-400'
                          : 'bg-success-100 dark:bg-success-900/30 text-success-600 dark:text-success-400'
                    }`}
                  >
                    {rec.priority === 'high' ? '!' : rec.priority === 'medium' ? 'i' : '✓'}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900 dark:text-gray-100">{rec.message}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 capitalize mt-1">
                      {rec.type} • {rec.priority} priority
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Question Details Section - Only shown for full access (UC-108) */}
      {accessLevel === 'full' && questions.length > 0 && (
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                Question Details
              </h3>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {filteredQuestions.length} of {questions.length} questions
              </span>
            </div>
          </div>
          <div className="card-body">
            {/* Quick Filters */}
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => handleQuickFilter('all')}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  filterCorrectness === 'all'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                All Questions
              </button>
              <button
                onClick={() => handleQuickFilter('correct')}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  filterCorrectness === 'correct'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                <CheckCircle className="w-4 h-4 inline mr-1" />
                All Right
              </button>
              <button
                onClick={() => handleQuickFilter('incorrect')}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  filterCorrectness === 'incorrect'
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                <XCircle className="w-4 h-4 inline mr-1" />
                All Wrong
              </button>
              {/* Each state is independently selectable (a cycle button overshoots / isn't discoverable).
                  Icon + select are siblings (no colored wrapper) to avoid a background seam. */}
              <div className="inline-flex items-center gap-1">
                <Calculator
                  className={`w-4 h-4 ${filterAltSolution !== 'all' ? 'text-indigo-600' : 'text-gray-500 dark:text-gray-400'}`}
                />
                <select
                  value={filterAltSolution}
                  onChange={(e) => setFilterAltSolution(e.target.value)}
                  title="Filter by alternative-solution availability"
                  className={`rounded-full px-3 py-1 text-sm font-medium cursor-pointer border-0 focus:ring-2 focus:ring-primary-500 ${
                    filterAltSolution !== 'all'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <option value="all">Alt solution: all</option>
                  <option value="any">Has alternative</option>
                  <option value="desmos">Desmos only</option>
                </select>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3 mb-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  placeholder="Search by ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Domain Filter */}
              <select
                value={filterDomain}
                onChange={(e) => setFilterDomain(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Domains</option>
                {uniqueDomains.map((domain) => (
                  <option key={domain} value={domain}>
                    {domain}
                  </option>
                ))}
              </select>

              {/* Skill Filter */}
              <select
                value={filterSkill}
                onChange={(e) => setFilterSkill(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Skills</option>
                {uniqueSkills.map((skill) => (
                  <option key={skill} value={skill}>
                    {skill}
                  </option>
                ))}
              </select>

              {/* Difficulty Filter */}
              <select
                value={filterDifficulty}
                onChange={(e) => setFilterDifficulty(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Difficulties</option>
                {uniqueDifficulties.map((diff) => (
                  <option key={diff} value={diff}>
                    {diff === 'H' ? 'Hard' : diff === 'M' ? 'Medium' : 'Easy'}
                  </option>
                ))}
              </select>

              {/* Stage Filter */}
              <select
                value={filterStage}
                onChange={(e) => setFilterStage(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Stages</option>
                {uniqueStages.map((stage) => (
                  <option key={stage} value={stage}>
                    {stage}
                  </option>
                ))}
              </select>

              {/* Clear Filters */}
              <button
                onClick={() => {
                  setSearchTerm('')
                  setFilterDomain('all')
                  setFilterSkill('all')
                  setFilterDifficulty('all')
                  setFilterStage('all')
                  setFilterCorrectness('all')
                }}
                className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Clear Filters
              </button>
            </div>

            {/* Questions List */}
            {questionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner size="md" />
              </div>
            ) : filteredQuestions.length === 0 ? (
              <div className="text-center py-8 text-gray-600 dark:text-gray-400">
                No questions found matching your filters.
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredQuestions.map((question, index) => {
                  const isCorrect = question.isCorrect || question.is_correct
                  return (
                    <div
                      key={question.id || index}
                      onClick={() => handleQuestionClick(index)}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg cursor-pointer transition-colors border border-gray-200 dark:border-gray-600"
                    >
                      <div className="flex items-center space-x-3 flex-1">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            isCorrect
                              ? 'bg-green-100 dark:bg-green-900/30'
                              : 'bg-red-100 dark:bg-red-900/30'
                          }`}
                        >
                          {isCorrect ? (
                            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center flex-wrap gap-2">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              ID: {question.question_id || question.questionId || question.id}
                            </span>
                            {question.domain && (
                              <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded text-xs">
                                {question.domain?.domain_name || question.domain}
                              </span>
                            )}
                            {question.skill && (
                              <span className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 rounded text-xs">
                                {question.skill?.skill_name || question.skill}
                              </span>
                            )}
                            {question.difficulty && (
                              <span
                                className={`px-2 py-0.5 rounded text-xs ${
                                  question.difficulty === 'H'
                                    ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                                    : question.difficulty === 'M'
                                      ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                                      : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                }`}
                              >
                                {question.difficulty === 'H'
                                  ? 'Hard'
                                  : question.difficulty === 'M'
                                    ? 'Medium'
                                    : 'Easy'}
                              </span>
                            )}
                            {question.stageName && (
                              <span className="px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 rounded text-xs">
                                {question.stageName}
                              </span>
                            )}
                            {question.hasAlternativeSolution && (
                              <span
                                title="Has an alternative solution to the traditional (analytical) approach"
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 rounded text-xs"
                              >
                                <Calculator className="w-3 h-3" />
                                Alt solution
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <Eye className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Coach Feedback Section */}
      {session?.creator?.id && (
        <CoachFeedbackSection
          challengeId={effectiveChallengeId}
          studentId={session.creator.id}
          studentName={session.creator.name || creatorName}
        />
      )}

      {/* Actions */}
      <div className="card">
        <div className="card-footer">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Challenge completed in {formatTime(session.totalTime)}
            </div>
            <button onClick={() => navigate('/dashboard')} className="btn-secondary">
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      {/* Question Detail Modal */}
      <QuestionDetailModal
        isOpen={showQuestionModal}
        onClose={() => {
          setShowQuestionModal(false)
          setSelectedQuestionIndex(null)
        }}
        question={selectedQuestion}
        questions={filteredQuestions}
        currentIndex={selectedQuestionIndex}
        onNavigate={handleQuestionNavigate}
      />
    </div>
  )
}

export default ChallengeReport
